import './AppShell.scss';
import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AppShell as MantineAppShell } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { MdChevronLeft, MdChevronRight, MdPushPin } from 'react-icons/md';
import Drawer from '../../atoms/Drawer';
import { useUi } from '../../provider/UiContext';
import { AppShellContext, IAppShellApi } from './AppShellContext';
import { AppShellMenuToggle } from './AppShellMenuToggle';
import {
    APP_SHELL_TOGGLE_SELECTOR,
    APP_SHELL_TRANSITION_DURATION,
    HEADER_HEIGHT,
    NAVBAR_WIDTH_CLOSED,
    NAVBAR_WIDTH_OPEN,
    SIDEBAR_COMPACT_QUERY,
    SIDEBAR_OVERLAY_CLOSE_DELAY,
    SIDEBAR_OVERLAY_OPEN_DELAY,
    SIDEBAR_PHONE_QUERY,
} from './appShellConstants';
import { resolveSidebar } from './sidebarMode';
import {
    clampSidebarWidth,
    readSidebarPinned,
    readSidebarWidth,
    saveSidebarPreference,
    sidebarStorageKeys,
    SIDEBAR_MAX_WIDTH,
    SIDEBAR_MIN_WIDTH,
    SIDEBAR_STORAGE_KEY,
} from './sidebarStorage';
import { useInitialReveal } from '../../hooks/useInitialReveal';

export interface IAppShellProps {
    /** Страница */
    children: ReactNode;
    /** Шапка справа от кнопки меню; функция получает состояние меню — для своей кнопки меню в шапке */
    header?: ReactNode | ((shell: IAppShellApi) => ReactNode);
    /**
     * Кнопка меню (бургер) оболочки в начале шапки. `false` — кнопку рисует шапка сама (`toggleSidebar`,
     * `sidebarToggleLabel`, `sidebarOpen` из `useAppShell()` или функции `header`) и помечает её
     * `data-app-shell-toggle` либо классом `menu-toggle-button`.
     * @default true
     */
    menuToggle?: boolean;
    /** Содержимое панели меню — обычно `SidebarMenu` */
    sidebar?: ReactNode;
    /** Фон панели под содержимым (`SidebarMascots`) */
    sidebarDecoration?: ReactNode;
    /** Плашки над страницей на всю ширину (браузер, релиз-новости): с ними тень шапки переезжает под них */
    notes?: ReactNode;
    /** Экран загрузки поверх страницы, пока оболочка проявляется (первые 500 мс) */
    startup?: ReactNode;
    /** Окна и слои рядом с оболочкой (туры, окна приложения) */
    overlays?: ReactNode;
    /**
     * Страница умеет телефонную раскладку: на узком экране (< 48em) меню — Drawer, а окно не держит десктопную
     * ширину 1280 px. Без неё экран уже 1280 px получает вьюпорт `width=1280`.
     */
    phoneLayout?: boolean;
    /** Ключ текущего адреса (`location.key` роутера): переход закрывает временную панель и Drawer */
    locationKey?: string;
    /** Меню закреплено — управляемое значение; без него оболочка хранит его сама (`<storageKey>.pinned`) */
    pinned?: boolean;
    /** Пользователь закрепил или открепил меню */
    onPinnedChange?: (pinned: boolean) => void;
    /**
     * Основа ключей localStorage меню: ширина `<storageKey>.width`, закрепление `<storageKey>.pinned`, закреплённые
     * пункты `<storageKey>.pins.<scope>`; перед основой — `storageNamespace` из `UiProvider`.
     * @default 'srm.sidebar'
     */
    storageKey?: string;
    /** Класс корня (вариант оформления приложения) */
    className?: string;
    /** data-атрибуты корня */
    rootAttributes?: Record<`data-${string}`, string | undefined>;
}

const APP_SHELL_STYLE = {
    '--sidebar-expanded-width': `${NAVBAR_WIDTH_OPEN}px`,
    '--sidebar-collapsed-width': `${NAVBAR_WIDTH_CLOSED}px`,
} as CSSProperties;

const noop = () => undefined;

const toggleSource =
    (source: string, active: boolean) =>
    (prev: ReadonlySet<string>): ReadonlySet<string> => {
        if (prev.has(source) === active) return prev;
        const next = new Set(prev);
        if (active) next.add(source);
        else next.delete(source);
        return next;
    };

const toggleZoneOf = (target: EventTarget | null) =>
    target instanceof Element ? target.closest(APP_SHELL_TOGGLE_SELECTOR) : null;

/**
 * Каркас приложения: шапка, боковое меню (закреплённое, рейл с временной панелью поверх страницы, на телефоне —
 * Drawer), лист страницы. Меню закрепляется и меняет ширину, настройки — в localStorage приложения. Временная панель
 * открывается наведением на рейл и закрывается уходом курсора, щелчком мимо, Escape и переходом; её держат фокус
 * клавиатуры и ввод внутри, перетаскивание ширины, открытые из неё окна (`useSidebarHold`). Ctrl+\ (⌘+\) — свернуть
 * или развернуть меню; ширину меняют перетаскиванием разделителя или стрелками и Home/End на нём. На время полного
 * экрана страница скрывает оболочку (`useShellFocus`): меню и шапка уходят за край, с клавиатуры они недоступны.
 */
const AppShell = ({
    children,
    header,
    menuToggle = true,
    sidebar,
    sidebarDecoration,
    notes,
    startup,
    overlays,
    phoneLayout = false,
    locationKey,
    pinned: pinnedProp,
    onPinnedChange,
    storageKey = SIDEBAR_STORAGE_KEY,
    className,
    rootAttributes,
}: IAppShellProps) => {
    const { storageNamespace } = useUi();
    const keys = useMemo(() => sidebarStorageKeys(storageNamespace, storageKey), [storageNamespace, storageKey]);
    const [storedPinned, setStoredPinned] = useState(() => readSidebarPinned(keys.pinned));
    const pinnedPreference = pinnedProp ?? storedPinned;
    const isEntering = useInitialReveal();
    const narrow = useMediaQuery(SIDEBAR_PHONE_QUERY, false, { getInitialValueInEffect: false });
    const mobile = phoneLayout && narrow;
    const compact = useMediaQuery(SIDEBAR_COMPACT_QUERY, false, { getInitialValueInEffect: false });
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [holds, setHolds] = useState<ReadonlySet<string>>(() => new Set());
    const popupOpen = holds.size > 0;
    const [focusSources, setFocusSources] = useState<ReadonlySet<string>>(() => new Set());
    const focused = focusSources.size > 0;
    const [sidebarWidth, setSidebarWidth] = useState(() => readSidebarWidth(keys.width));
    const [resizing, setResizing] = useState(false);
    // Ширина последнего движения: отпускание сохраняет её, даже если рендер ещё не догнал pointermove.
    const dragWidth = useRef(sidebarWidth);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const resizeRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const openTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    // Last input device. A closed dialog returns focus to its trigger with :focus-visible,
    // so the pseudo-class alone cannot tell keyboard navigation from a pointer session.
    const keyboardInput = useRef(false);
    const {
        mode,
        pinned,
        open: menuOpen,
    } = resolveSidebar({ mobile, compact, pinnedPreference, overlayOpen, mobileMenuOpen });
    const shellStyle = { ...APP_SHELL_STYLE, '--sidebar-expanded-width': `${sidebarWidth}px` } as CSSProperties;

    const setPinnedPreference = useCallback(
        (value: boolean) => {
            if (pinnedProp === undefined) {
                setStoredPinned(value);
                saveSidebarPreference(keys.pinned, value);
            }
            onPinnedChange?.(value);
        },
        [pinnedProp, keys.pinned, onPinnedChange],
    );
    const holdSidebar = useCallback((source: string, active: boolean) => setHolds(toggleSource(source, active)), []);
    const focusShell = useCallback(
        (source: string, active: boolean) => setFocusSources(toggleSource(source, active)),
        [],
    );

    const requestOpen = useCallback(() => {
        if (!pinned && !mobile) setOverlayOpen(true);
    }, [pinned, mobile]);
    const closeOverlay = useCallback(() => setOverlayOpen(false), []);
    const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
    const pointerInSidebar = useCallback(
        () =>
            sidebarRef.current?.matches(':hover') ||
            resizeRef.current?.matches(':hover') ||
            [...(headerRef.current?.querySelectorAll(APP_SHELL_TOGGLE_SELECTOR) ?? [])].some((zone) =>
                zone.matches(':hover'),
            ),
        [],
    );
    const cancelClose = useCallback(() => clearTimeout(closeTimer.current), []);
    const clearTimers = useCallback(() => {
        clearTimeout(openTimer.current);
        clearTimeout(closeTimer.current);
    }, []);
    const scheduleClose = useCallback(() => {
        clearTimers();
        closeTimer.current = setTimeout(() => {
            if (
                !popupOpen &&
                !resizing &&
                !pointerInSidebar() &&
                // Typing or keyboard navigation inside the panel keeps it open; mouse clicks do not.
                !sidebarRef.current?.querySelector('input:focus') &&
                !(keyboardInput.current && sidebarRef.current?.contains(document.activeElement)) &&
                document.activeElement !== resizeRef.current
            )
                setOverlayOpen(false);
        }, SIDEBAR_OVERLAY_CLOSE_DELAY);
    }, [clearTimers, popupOpen, resizing, pointerInSidebar]);

    useEffect(() => {
        setOverlayOpen(false);
    }, [locationKey, mobile, pinned, focused]);
    useEffect(() => clearTimers, [clearTimers]);
    useEffect(() => {
        const key = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') keyboardInput.current = true;
        };
        const pointer = () => {
            keyboardInput.current = false;
        };
        document.addEventListener('keydown', key, true);
        document.addEventListener('pointerdown', pointer, true);
        return () => {
            document.removeEventListener('keydown', key, true);
            document.removeEventListener('pointerdown', pointer, true);
        };
    }, []);
    useEffect(() => {
        if (!overlayOpen || mobile) return;
        const outside = (event: PointerEvent) => {
            if (
                !popupOpen &&
                !resizing &&
                !sidebarRef.current?.contains(event.target as Node) &&
                !(event.target as Element).closest(`.sidebar-resize, ${APP_SHELL_TOGGLE_SELECTOR}`)
            )
                setOverlayOpen(false);
        };
        const escape = (event: KeyboardEvent) => {
            // Escape в окне (и в окне не из меню) закрывает окно, а не панель.
            if (event.key === 'Escape' && !popupOpen && !document.querySelector('[role="dialog"]')) {
                setOverlayOpen(false);
                sidebarRef.current?.querySelector<HTMLButtonElement>('.sidebar-toggle')?.focus();
            }
        };
        document.addEventListener('pointerdown', outside);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('pointerdown', outside);
            document.removeEventListener('keydown', escape);
        };
    }, [overlayOpen, mobile, popupOpen, resizing]);

    useEffect(() => setMobileMenuOpen(false), [locationKey, mobile]);

    const toggleClick = useCallback(() => {
        if (mobile) setMobileMenuOpen((open) => !open);
        else {
            clearTimers();
            if (overlayOpen) {
                // A narrow window cannot pin: the bottom button only closes the temporary panel.
                if (mode === 'wide') setPinnedPreference(true);
                setOverlayOpen(false);
            } else if (!pinned) setOverlayOpen(true);
            else setPinnedPreference(false);
        }
    }, [mobile, mode, overlayOpen, pinned, setPinnedPreference, clearTimers]);

    // The header button only pins or unpins: a hover-opened overlay is pinned in place.
    // In a narrow window it shows or hides the temporary panel instead, like the drawer on phones.
    const headerToggleClick = useCallback(() => {
        if (mobile) setMobileMenuOpen((open) => !open);
        else {
            clearTimers();
            if (mode === 'compact') setOverlayOpen((open) => !open);
            else {
                setPinnedPreference(!pinned);
                setOverlayOpen(false);
            }
        }
    }, [mobile, mode, pinned, setPinnedPreference, clearTimers]);

    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (
                (event.metaKey || event.ctrlKey) &&
                event.code === 'Backslash' &&
                !focused &&
                !popupOpen &&
                !document.querySelector('[role="dialog"]')
            ) {
                event.preventDefault();
                if (overlayOpen) setOverlayOpen(false);
                else toggleClick();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [toggleClick, overlayOpen, popupOpen, focused]);

    // Resume the close countdown when a popup closes, not when the overlay itself opens.
    const wasPopupOpen = useRef(false);
    useEffect(() => {
        if (popupOpen) clearTimeout(closeTimer.current);
        else if (wasPopupOpen.current) {
            // Focus returned by the closed dialog is not navigation inside the panel.
            keyboardInput.current = false;
            if (overlayOpen && !pointerInSidebar()) scheduleClose();
        }
        wasPopupOpen.current = popupOpen;
    }, [popupOpen, overlayOpen, scheduleClose, pointerInSidebar]);

    useEffect(() => {
        const meta = document.querySelector('meta[name="viewport"]');
        if (!meta) return undefined;

        const applyViewport = () => {
            // Страницам без телефонной раскладки нужен десктопный вьюпорт. Метрика — screen.width (физический
            // экран): она не зависит от самого meta-тега, в отличие от innerWidth / matchMedia, иначе возникла бы
            // петля переключения.
            meta.setAttribute(
                'content',
                !phoneLayout && window.screen.width < 1280 ? 'width=1280' : 'width=device-width, initial-scale=1',
            );
        };

        applyViewport();
        // Планшеты при повороте могут пересекать порог 1280 — переоцениваем вьюпорт.
        window.addEventListener('orientationchange', applyViewport);

        return () => window.removeEventListener('orientationchange', applyViewport);
    }, [phoneLayout]);

    const canPinOverlay = overlayOpen && mode === 'wide';
    const bottomToggleLabel = canPinOverlay ? 'Закрепить меню' : menuOpen ? 'Свернуть меню' : 'Развернуть меню';
    const headerToggleLabel =
        mode === 'drawer'
            ? menuOpen
                ? 'Свернуть меню'
                : 'Открыть меню'
            : menuOpen
              ? 'Свернуть боковое меню'
              : mode === 'wide'
                ? 'Закрепить боковое меню'
                : 'Открыть боковое меню';

    const api = useMemo<IAppShellApi>(
        () => ({
            sidebarMode: mode,
            sidebarPinned: pinned,
            sidebarOpen: menuOpen,
            toggleSidebar: headerToggleClick,
            sidebarToggleLabel: headerToggleLabel,
            requestSidebarOpen: requestOpen,
            onSidebarNavigate: closeOverlay,
            holdSidebar,
            focusShell,
            storageKey,
        }),
        [
            mode,
            pinned,
            menuOpen,
            headerToggleClick,
            headerToggleLabel,
            requestOpen,
            closeOverlay,
            holdSidebar,
            focusShell,
            storageKey,
        ],
    );
    // В Drawer меню развёрнуто целиком, переход закрывает Drawer.
    const drawerApi = useMemo<IAppShellApi>(
        () => ({ ...api, sidebarOpen: true, requestSidebarOpen: noop, onSidebarNavigate: closeMobileMenu }),
        [api, closeMobileMenu],
    );

    const saveWidth = (width: number) => {
        dragWidth.current = width;
        setSidebarWidth(width);
        saveSidebarPreference(keys.width, width);
    };

    return (
        <AppShellContext.Provider value={api}>
            <MantineAppShell
                {...rootAttributes}
                className={clsx(
                    'app-shell',
                    overlayOpen && !pinned && !mobile && 'app-shell--overlay',
                    resizing && 'app-shell--resizing',
                    className,
                )}
                data-entering={isEntering}
                data-focus={focused || undefined}
                header={{ height: HEADER_HEIGHT, collapsed: focused }}
                navbar={{
                    width: pinned ? sidebarWidth : NAVBAR_WIDTH_CLOSED,
                    breakpoint: 0,
                    collapsed: { desktop: mobile || focused },
                }}
                padding={0}
                transitionDuration={isEntering || resizing ? 0 : APP_SHELL_TRANSITION_DURATION}
                transitionTimingFunction="ease"
                style={shellStyle}
            >
                <MantineAppShell.Header
                    className="header-navbar"
                    ref={headerRef}
                    inert={focused}
                    // Зона кнопки меню (своей или шапки) держит временную панель, как сама панель.
                    onPointerOver={(event) => {
                        const zone = toggleZoneOf(event.target);
                        if (zone && !zone.contains(event.relatedTarget as Node | null)) cancelClose();
                    }}
                    onPointerOut={(event) => {
                        const zone = toggleZoneOf(event.target);
                        if (zone && !zone.contains(event.relatedTarget as Node | null)) scheduleClose();
                    }}
                    onBlur={(event) => {
                        if (toggleZoneOf(event.target)) scheduleClose();
                    }}
                >
                    {menuToggle && <AppShellMenuToggle />}
                    {typeof header === 'function' ? header(api) : header}
                </MantineAppShell.Header>

                <MantineAppShell.Navbar
                    className="sidebar"
                    aria-label="Разделы"
                    ref={sidebarRef}
                    inert={focused}
                    onPointerEnter={(event) => {
                        clearTimeout(closeTimer.current);
                        if (event.pointerType === 'mouse' && !pinned && !mobile) {
                            clearTimeout(openTimer.current);
                            openTimer.current = setTimeout(() => setOverlayOpen(true), SIDEBAR_OVERLAY_OPEN_DELAY);
                        }
                    }}
                    onPointerLeave={scheduleClose}
                    onBlur={(event) => {
                        if (
                            !event.currentTarget.contains(event.relatedTarget) &&
                            !event.currentTarget.matches(':hover')
                        )
                            scheduleClose();
                    }}
                >
                    {!mobile && sidebarDecoration}
                    {!mobile && sidebar}
                    {!mobile && (
                        <div className={clsx('sidebar-bottom', !menuOpen && 'sidebar-bottom--rail')}>
                            <button
                                type="button"
                                className="sidebar-toggle"
                                data-testid="sidebar-toggle-button"
                                onClick={toggleClick}
                                aria-label={bottomToggleLabel}
                                title={`${bottomToggleLabel} (Ctrl+\\)`}
                            >
                                {canPinOverlay ? <MdPushPin /> : menuOpen ? <MdChevronLeft /> : <MdChevronRight />}
                                {menuOpen && <span>{bottomToggleLabel}</span>}
                            </button>
                        </div>
                    )}
                </MantineAppShell.Navbar>
                {!mobile && !focused && menuOpen && (
                    // Focusable separators are the ARIA window-splitter pattern.
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                    <div
                        ref={resizeRef}
                        role="separator"
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard-operable ARIA window splitter.
                        tabIndex={0}
                        className="sidebar-resize"
                        aria-label="Ширина меню"
                        aria-orientation="vertical"
                        aria-valuemin={SIDEBAR_MIN_WIDTH}
                        aria-valuemax={SIDEBAR_MAX_WIDTH}
                        aria-valuenow={sidebarWidth}
                        style={{ left: sidebarWidth - 7 }}
                        onPointerEnter={cancelClose}
                        onPointerLeave={scheduleClose}
                        onFocus={cancelClose}
                        onBlur={scheduleClose}
                        onPointerDown={(event) => {
                            event.preventDefault();
                            clearTimeout(closeTimer.current);
                            dragWidth.current = sidebarWidth;
                            setResizing(true);
                            event.currentTarget.setPointerCapture(event.pointerId);
                        }}
                        onPointerMove={(event) => {
                            if (!resizing) return;
                            dragWidth.current = clampSidebarWidth(event.clientX);
                            setSidebarWidth(dragWidth.current);
                        }}
                        onPointerUp={() => {
                            if (!resizing) return;
                            setResizing(false);
                            saveWidth(dragWidth.current);
                        }}
                        onLostPointerCapture={() => setResizing(false)}
                        onKeyDown={(event) => {
                            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                            event.preventDefault();
                            saveWidth(
                                clampSidebarWidth(
                                    event.key === 'Home'
                                        ? SIDEBAR_MIN_WIDTH
                                        : event.key === 'End'
                                          ? SIDEBAR_MAX_WIDTH
                                          : sidebarWidth + (event.key === 'ArrowRight' ? 4 : -4),
                                ),
                            );
                        }}
                    />
                )}

                <MantineAppShell.Main className="app-shell__main">
                    <div className="main-box">
                        <div className="notes">{notes}</div>
                        <div className="main-content">{children}</div>
                    </div>
                </MantineAppShell.Main>
                {isEntering && startup && (
                    <div className="app-shell__startup-cover" aria-hidden="true">
                        {startup}
                    </div>
                )}
            </MantineAppShell>

            {mobile && (
                <Drawer
                    opened={mobileMenuOpen}
                    onClose={closeMobileMenu}
                    header="Разделы"
                    position="left"
                    width={NAVBAR_WIDTH_OPEN}
                    withOverlay
                    className="app-shell__drawer"
                    contentClassName="app-shell__drawer-content"
                >
                    <AppShellContext.Provider value={drawerApi}>
                        <nav className="sidebar app-shell__mobile-menu" aria-label="Разделы" style={APP_SHELL_STYLE}>
                            {sidebarDecoration}
                            {sidebar}
                        </nav>
                    </AppShellContext.Provider>
                </Drawer>
            )}
            {overlays}
        </AppShellContext.Provider>
    );
};

export default AppShell;
